import { z } from 'zod';

import { zodEnum, zodTimeStamp } from '@/utils/zodUtils';

export const ANNOUNCEMENT_TEXT_MAX_LENGTH = 400;
export const ANNOUNCEMENT_TITLE_MAX_LENGTH = 60;

export const ANNOUNCEMENT_POSITIONS = [
  'modal',
  'page_top',
  'card_top',
  'after_address',
  'before_form',
] as const;

export const ANNOUNCEMENT_SEVERITIES = [
  'notice',
  'advisory',
  'critical',
] as const;

export const ANNOUNCEMENT_EMPHASES = ['standard', 'bold'] as const;

export type AnnouncementPosition = (typeof ANNOUNCEMENT_POSITIONS)[number];
export type AnnouncementSeverity = (typeof ANNOUNCEMENT_SEVERITIES)[number];
export type AnnouncementEmphasis = (typeof ANNOUNCEMENT_EMPHASES)[number];

export const ANNOUNCEMENT_POSITION_LABELS: Record<
  AnnouncementPosition,
  string
> = {
  modal: 'Modal',
  page_top: 'Top of Page',
  card_top: 'Top of Card',
  after_address: 'After Address',
  before_form: 'Before Form',
};

export const ANNOUNCEMENT_SEVERITY_LABELS: Record<
  AnnouncementSeverity,
  string
> = {
  notice: 'Notice',
  advisory: 'Advisory',
  critical: 'Critical',
};

export const ANNOUNCEMENT_SEVERITY_DESCRIPTIONS: Record<
  AnnouncementSeverity,
  string
> = {
  notice:
    'General information customers might find useful (e.g., new lot opening).',
  advisory:
    'Planned change worth knowing about ahead of time (e.g., closure on Friday).',
  critical:
    'Consequences if ignored (e.g., vehicles will be towed). Use sparingly.',
};

export const ANNOUNCEMENT_EMPHASIS_DESCRIPTIONS: Record<
  AnnouncementEmphasis,
  string
> = {
  standard:
    'Tinted background with a colored border. Default for most announcements.',
  bold: 'Solid filled background with white text. Maximum visual prominence.',
};

export const ANNOUNCEMENT_EMPHASIS_LABELS: Record<
  AnnouncementEmphasis,
  string
> = {
  standard: 'Standard',
  bold: 'Bold',
};

export const LocationAnnouncementSchema = z.object({
  id: z.string().uuid(),
  location_id: z.string(),
  block_face_id: z.string().uuid().nullish(),
  text: z.string(),
  title: z.string().nullish(),
  starts_at: zodTimeStamp.nullish(),
  ends_at: zodTimeStamp.nullish(),
  position: z.enum(zodEnum(ANNOUNCEMENT_POSITIONS)).catch('card_top'),
  severity: z.enum(zodEnum(ANNOUNCEMENT_SEVERITIES)).catch('notice'),
  emphasis: z.enum(zodEnum(ANNOUNCEMENT_EMPHASES)).catch('standard'),
  dismissible: z.boolean().nullish(),
  should_always_show: z.boolean().default(false),
  created_at: zodTimeStamp,
  updated_at: zodTimeStamp,
});

export type LocationAnnouncement = z.infer<typeof LocationAnnouncementSchema>;

export const LocationAnnouncementListSchema = z.array(
  LocationAnnouncementSchema
);

export type LocationAnnouncementList = z.infer<
  typeof LocationAnnouncementListSchema
>;

export type CreateLocationAnnouncementPayload = {
  block_face_id?: string | null;
  text: string;
  title?: string | null;
  starts_at?: string | null;
  ends_at?: string | null;
  position: AnnouncementPosition;
  severity: AnnouncementSeverity;
  emphasis?: AnnouncementEmphasis;
  dismissible?: boolean | null;
  should_always_show?: boolean;
};

export type UpdateLocationAnnouncementPayload = {
  text?: string;
  title?: string | null;
  clear_title?: boolean;
  starts_at?: string | null;
  clear_starts_at?: boolean;
  ends_at?: string | null;
  clear_ends_at?: boolean;
  position?: AnnouncementPosition;
  severity?: AnnouncementSeverity;
  emphasis?: AnnouncementEmphasis;
  dismissible?: boolean | null;
  clear_dismissible?: boolean;
  should_always_show?: boolean;
};
